import React, { useCallback, useState } from "react";
import Link from "next/link";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { Pagination } from "swiper/modules";
import "swiper/css/pagination";
import "swiper/css/navigation";
import styles from "./TransformativeHome.module.css";
import { Description } from "@/utils/utils";
import { ChevronRight, ArrowRight } from "lucide-react";

interface TitleDescription {
  id: number;
  title: string;
  description: string;
}

interface LinkData {
  id: number;
  label: string;
  url: string;
}

interface Card {
  id: number;
  title_description: TitleDescription;
  link: LinkData;
}

interface TransformativeLearning {
  id: number;
  title_description: TitleDescription;
  cards: Card[];
}

interface Props {
  data: TransformativeLearning;
  page?: string;
}

const CriticalText = ({
  title,
  description,
}: {
  title?: string;
  description?: string;
}) => (
  <div className={styles.criticalText}>
    {title && <h2 className={styles.mobItem}>{title}</h2>}
    {description && <p className={styles.mobItem}>{description}</p>}
  </div>
);

const TransformativeHome: React.FC<Props> = ({ data, page }) => {
  const { title_description, cards } = data;
  const [activeAccordion, setActiveAccordion] = useState<number | null>(null);
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  const handleAccordionClick = useCallback((id: number) => {
    setActiveAccordion((prev) => (prev === id ? null : id));
  }, []);

  const MemoizedDescription = React.memo(Description);

  const handleMouseEnter = useCallback((id: number) => setHoveredCard(id), []);
  const handleMouseLeave = useCallback(() => setHoveredCard(null), []);

  // Set initial hovered card after component mounts
  React.useEffect(() => {
    if (cards && cards.length > 0) {
      setHoveredCard(cards[0]?.id || null);
    }
  }, [cards]);

  return (
    <div
      className={`${
        page === "alumni"
          ? styles.alumniTransformative
          : page === "faculty"
          ? styles.facultyTransformative
          : page === "home"
          ? "" // No additional class for home page
          : styles.aboutTransformative
      }`}
      id={page === "alumni" ? "AlumniLifelong" : undefined}
      data-section="Transformative"
    >
      <div className="container">
        <div className={styles.TransformativeLearning}>
          {title_description?.description && (
            <CriticalText
              title={title_description?.title}
              description={title_description?.description}
            />
          )}
          {/* Desktop View */}
          {page === "alumni" ? (
            <div
              className={`${styles.alumniGrid} ${
                cards.length === 4 ? styles.fourColumns : ""
              } d-none d-md-flex`}
              style={{
                minHeight: "400px",
                contain: "layout style paint",
              }}
            >
              {cards.length > 4 ? (
                <Swiper
                  slidesPerView={4}
                  spaceBetween={32}
                  modules={[Pagination]}
                  pagination={{ clickable: true }}
                  className={styles.swiperStyle}
                  breakpoints={{
                    320: {
                      slidesPerView: 1,
                      spaceBetween: 24,
                    },
                    768: {
                      slidesPerView: 2,
                      spaceBetween: 24,
                    },
                    992: {
                      slidesPerView: 3,
                      spaceBetween: 32,
                    },
                    1200: {
                      slidesPerView: 4,
                      spaceBetween: 32,
                    },
                  }}
                >
                  {cards?.map((programme) => (
                    <SwiperSlide
                      key={programme?.id}
                      className={styles.programBox}
                      style={{
                        aspectRatio: "1/1.2",
                        contain: "layout style paint",
                      }}
                    >
                      <div
                        className={`${styles.card} ${
                          hoveredCard === programme?.id ? styles.hovered : ""
                        }`}
                        onMouseEnter={() => handleMouseEnter(programme?.id)}
                        onMouseLeave={handleMouseLeave}
                        style={{
                          aspectRatio: "1/1.2",
                          contain: "layout style paint",
                        }}
                      >
                        <strong>{programme?.title_description?.title}</strong>
                        <MemoizedDescription
                          text={programme?.title_description?.description}
                          style={styles.description}
                        />
                        <Link
                          href={programme?.link?.url || "#"}
                          target="_blank"
                          prefetch={false}
                          className={styles.link}
                          passHref
                          shallow
                          title={programme?.link?.label}
                        >
                          {programme?.link?.label} <ArrowRight />
                        </Link>
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              ) : (
                cards?.map((programme) => (
                  <div
                    key={programme?.id}
                    className={styles.programBox}
                    style={{
                      aspectRatio: "1/1.2",
                      contain: "layout style paint",
                    }}
                  >
                    <div
                      className={`${styles.card} ${
                        hoveredCard === programme?.id ? styles.hovered : ""
                      }`}
                      onMouseEnter={() => handleMouseEnter(programme?.id)}
                      onMouseLeave={handleMouseLeave}
                    >
                      <strong>{programme?.title_description?.title}</strong>
                      <MemoizedDescription
                        text={programme?.title_description?.description}
                        style={styles.description}
                      />
                      <Link
                        href={programme?.link?.url || "#"}
                        target="_blank"
                        prefetch={false}
                        className={styles.link}
                        passHref
                        shallow
                        title={programme?.link?.label}
                      >
                        {programme?.link?.label} <ArrowRight />
                      </Link>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            <div
              className={`${styles.regularGrid} ${
                cards.length > 3 ? styles.fourColumns : styles.threeColumns
              } d-none d-md-flex`}
              style={{
                minHeight: "400px",
                contain: "layout style paint",
              }}
            >
              {cards.length > 4 ? (
                <Swiper
                  slidesPerView={4}
                  spaceBetween={32}
                  modules={[Pagination]}
                  pagination={{ clickable: true }}
                  className={styles.swiperStyle}
                  breakpoints={{
                    320: {
                      slidesPerView: 1,
                      spaceBetween: 24,
                    },
                    768: {
                      slidesPerView: 2,
                      spaceBetween: 24,
                    },
                    992: {
                      slidesPerView: 3,
                      spaceBetween: 32,
                    },
                    1200: {
                      slidesPerView: 4,
                      spaceBetween: 32,
                    },
                  }}
                >
                  {cards?.map((programme) => (
                    <SwiperSlide
                      key={programme?.id}
                      className={styles.programBox}
                      style={{
                        aspectRatio: "1/1.2",
                        contain: "layout style paint",
                      }}
                    >
                      <div
                        className={`${styles.card} ${
                          hoveredCard === programme?.id ? styles.hovered : ""
                        }`}
                        onMouseEnter={() => handleMouseEnter(programme?.id)}
                        onMouseLeave={handleMouseLeave}
                        style={{
                          aspectRatio: "1/1.2",
                          contain: "layout style paint",
                        }}
                      >
                        <strong>{programme?.title_description?.title}</strong>
                        <MemoizedDescription
                          text={programme?.title_description?.description}
                          style={styles.description}
                        />
                        <Link
                          href={programme?.link?.url || "#"}
                          target="_blank"
                          className={styles.link}
                          prefetch={false}
                          passHref
                          shallow
                          title={programme?.link?.label}
                        >
                          {programme?.link?.label} <ArrowRight />
                        </Link>
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              ) : (
                cards?.map((programme) => (
                  <div
                    key={programme?.id}
                    className={styles.programBox}
                    style={{
                      aspectRatio: "1/1.2",
                      contain: "layout style paint",
                    }}
                  >
                    <div
                      className={`${styles.card} ${
                        hoveredCard === programme?.id ? styles.hovered : ""
                      }`}
                      onMouseEnter={() => handleMouseEnter(programme?.id)}
                      onMouseLeave={handleMouseLeave}
                    >
                      <strong>{programme?.title_description?.title}</strong>
                      <MemoizedDescription
                        text={programme?.title_description?.description}
                        style={styles.description}
                      />
                      <Link
                        href={programme?.link?.url || "#"}
                        target="_blank"
                        prefetch={false}
                        className={styles.link}
                        passHref
                        shallow
                        title={programme?.link?.label}
                      >
                        {programme?.link?.label} <ArrowRight />
                      </Link>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
          {/* Mobile Accordion View */}
          <div
            className="d-md-none"
            style={{
              minHeight: "200px",
              contain: "layout style paint",
            }}
          >
            <div className="accordion" id="accordionExample">
              {cards?.map((programme, index) => (
                <div className="accordion-item" key={programme?.id}>
                  <h3
                    className="accordion-header"
                    id={`heading${programme?.id}`}
                  >
                    <Link
                      href={programme?.link?.url || "#"}
                      className={styles.link}
                      passHref
                      shallow
                      title={programme?.title_description?.title}
                    >
                      <button
                        className={`accordion-button ${
                          index !== data?.cards?.length - 1
                            ? styles.bottomBorder
                            : ""
                        }`}
                        data-bs-toggle="collapse"
                        data-bs-target={`#collapse${programme?.id}`}
                        aria-expanded={activeAccordion === programme?.id}
                        aria-controls={`collapse${programme?.id}`}
                        onClick={() => handleAccordionClick(programme?.id)}
                        type="button"
                      >
                        <strong>{programme?.title_description?.title}</strong>
                        <span className={`${styles.accordionIcon} ms-auto`}>
                          <ChevronRight />
                        </span>
                      </button>
                    </Link>
                  </h3>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransformativeHome;
